import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
  {
    category: "General",
    question: "What is Algo Forex EA?",
    answer: "Algo Forex EA is a curated marketplace that offers premium MetaTrader 4 & 5 Expert Advisors (EAs) at highly accessible prices. We acquire top-rated automated systems, remove restrictive account-locking licenses, and distribute them to retail traders."
  },
  {
    category: "General",
    question: "How do I download my Expert Advisor after payment?",
    answer: "Downloads are instant. Immediately after purchase, a download button will be generated in your dashboard. You will also receive an automated email containing secure download links."
  },
  {
    category: "General",
    question: "Can you source a specific EA that isn't listed on your store?",
    answer: "Yes, we have a custom sourcing department. Submit a request via our Request EA page, and we will source and unlock the file for you."
  },
  {
    category: "Technical",
    question: "Can I use MT4 EAs on MetaTrader 5?",
    answer: "No, MT4 and MT5 use different programming languages (MQL4 vs MQL5). These specific files are compiled for MetaTrader 4 only. If you use MT5, please browse our MT5 category."
  },
  {
    category: "Technical",
    question: "How do I install an EA on MetaTrader 4 or MT5?",
    answer: "Open your terminal, go to File > Open Data Folder, navigate to MQL4/MQL5 > Experts, and paste the downloaded file there. Restart the terminal, drag the EA onto your chart, and ensure \"Allow Algo Trading\" is checked."
  },
  {
    category: "Technical",
    question: "Do these EAs work on prop firm accounts?",
    answer: "Yes, most of our EAs are compatible with prop firm servers. Check the specific product description for prop-safety certifications."
  },
  {
    category: "Technical",
    question: "Are these EAs fully unlocked?",
    answer: "Yes, all our EAs are fully unlocked binaries (.ex4/.ex5) with no account restrictions or licensing limits. You can run them on unlimited demo and live accounts."
  }
];

const FAQ = () => {
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    if (activeFaq === index) {
      setActiveFaq(null);
    } else {
      setActiveFaq(index);
    }
  };

  return (
    <main className="min-h-screen relative pt-24 pb-24">
      {/* Ambient background glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] -z-10 pointer-events-none"></div>
      
      <section className="max-w-4xl mx-auto px-margin-mobile md:px-margin-desktop">
        
        {/* Breadcrumbs */}
        <div className="flex items-center space-x-2 text-[12px] text-on-surface-variant mb-12">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span>/</span>
          <span className="text-silver-text">FAQ</span>
        </div>

        {/* Header */}
        <div className="text-center mb-16">
          <div className="bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded inline-block mb-6 border border-primary/20">
            Help Center
          </div>
          <h1 className="font-display-lg text-[48px] md:text-[64px] font-bold text-silver-text mb-4 leading-tight">
            Frequently Asked <span className="text-primary">Questions</span>
          </h1>
          <p className="text-[14px] text-on-surface-variant max-w-2xl mx-auto">
            Have questions about licenses, installation, or setups? Browse our FAQs or search for specific terms.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isActive = activeFaq === index;
            return (
              <div 
                key={index}
                className={`bg-[#1A1B20] border ${isActive ? 'border-primary/30' : 'border-white/5'} rounded-2xl overflow-hidden transition-colors duration-300`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 flex items-start text-left group"
                >
                  <span className="text-primary font-bold mr-3 mt-0.5">Q:</span>
                  <span className={`font-bold text-[15px] transition-colors duration-300 ${isActive ? 'text-primary' : 'text-silver-text group-hover:text-primary'} flex-1`}>
                    {faq.question}
                  </span>
                  <span className="hidden sm:inline-block text-primary/50 text-[10px] uppercase tracking-widest font-bold mt-1 ml-4 group-hover:text-primary transition-colors">
                    {faq.category}
                  </span>
                </button>
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="px-6 pb-6 pt-2 text-[14px] text-on-surface-variant leading-relaxed pl-[42px]">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Still Have Questions CTA */}
        <div className="mt-16 rounded-3xl border border-white/5 bg-[#1A1B20] p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="relative z-10 space-y-6">
            <h2 className="text-[24px] font-bold text-silver-text">Still Have Questions?</h2>
            <p className="text-[14px] text-on-surface-variant max-w-xl mx-auto leading-relaxed">
              If you didn't find what you were looking for, our team is always ready to assist. Reach out directly for personal setup help or general questions.
            </p>
            <div className="flex justify-center pt-4">
              <Link to="/contact" className="rounded-xl bg-transparent border border-white/10 text-silver-text px-8 py-3.5 font-bold text-[14px] hover:border-primary hover:text-primary transition-all duration-300">
                Contact Support
              </Link>
            </div>
          </div>
        </div>

      </section>
    </main>
  );
};

export default FAQ;
